import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useStore } from '@/models'

/**
 * useGameQuery - Scalable hook that handles discriminated union internally
 *
 * Exposes only safe, computed properties to components.
 * Components should use these computed values instead of accessing game state directly.
 * This prevents the need for type guards scattered throughout the codebase.
 */
export function useGameQuery(gameId: Id<'games'> | undefined, userId?: string) {
  const { session } = useStore()
  // Teacher/admin roles can see cards when visibility is allPrivate (any teacher, not only who toggled)
  const viewerIsTeacher = session.isTeacher || session.isAdmin || session.isSuperAdmin

  // Use the convenience query that combines game + players + available actions
  const gameState = useQuery(
    api.games.queries.getGameState,
    gameId && userId ? { gameId, userId, viewerIsTeacher } : 'skip'
  )

  const players = gameState?.players ?? []
  const isPaused = gameState?.isPaused ?? false

  // NOTE: Leave here for now - useful client side changes
  //
  // Correct players sanitized _with_ cards in backend.
  // Here we just show based on flow state what was sent.
  // const isTeacherShowingCards = 'default' === (gameState?.game.cardVisibilityMode?.mode ?? 'default')
  // const { isShowdownReveal = false, isWinnersReveal = false } = useActiveFlowSchedule(gameId, isPaused) || {} // UGH
  // const players =
  //   gameState?.players?.map((p) => {
  //     const { cards, ...rest } = p
  //     const viz = isShowdownReveal || (isWinnersReveal && isTeacherShowingCards)
  //     return { ...rest, cards: viz ? cards : [] }
  //   }) ?? []

  // Computed safe properties - handle discriminated union internally
  const isPlaying = gameState?.game?.state === 'playing'
  const isPregame = gameState?.game?.state === 'pregame'
  const isFinished = gameState?.game?.state === 'finished'

  // Safe access to currentPlayerId (null if not in playing state)
  // Type narrowing: check state === 'playing' to access currentPlayerId
  const currentPlayerId = gameState?.game?.state === 'playing' ? gameState.game.currentPlayerId : null

  const isMyTurn = currentPlayerId === gameState?.userPlayer?._id
  const myPlayer = gameState?.userPlayer

  // Host: creator (hostUserId) or seat 0 fallback. Teacher who created the game has no myPlayer but userId === hostUserId.
  const isHost = gameState?.game?.hostUserId
    ? userId === gameState.game.hostUserId
    : myPlayer?.seatId === 0

  // Teacher seat (non-playing observer above the table)
  const teacher = gameState?.game?.teacherUserId
    ? { userId: gameState.game.teacherUserId, name: gameState.game.teacherName ?? 'Teacher' }
    : null
  const isTeacherSeated = userId === gameState?.game?.teacherUserId

  // Host or seated teacher can start/resume the game (and should not see "Waiting for host")
  const canStartOrResumeGame = isHost || isTeacherSeated

  const callAmount = Math.min(
    (gameState?.biggestBet || 0) - (gameState?.userPlayer?.currentBet || 0),
    myPlayer?.chips || 0
  )

  return {
    ...gameState,
    availableActions: isPlaying ? gameState.availableActions : [],
    betHints: isPlaying ? gameState.betHints : [],
    players,
    callAmount,

    // Computed properties (use these instead of game.currentPlayerId)
    myPlayer,
    currentPlayerId, // Safe: null when not playing
    isMyTurn, // Safe: boolean
    isHost, // Safe: boolean - true if current user is game creator (hostUserId) or seat 0
    canStartOrResumeGame, // true if host or seated teacher — can start game and should not see "Waiting for host"
    teacher, // { userId, name } | null — seated teacher observer
    isTeacherSeated, // true if current user is the seated teacher

    // State helpers
    cards: myPlayer?.cards ?? [],
    isPlaying,
    isPregame,
    isFinished,
    isPaused
  }
}

export function useHandHistoryQuery(gameId: Id<'games'> | undefined) {
  const handHistory = useQuery(api.hands.queries.getHandHistory, gameId ? { limit: 15, gameId } : 'skip')
  return { handHistory }
}
